import { ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'

import CardOne from '@/components/common/card/CardOne'
import NormalTopBar from '@/components/common/bar/NormalTopBar'
import BottomNavBar from '@/components/common/bar/BottomNavBar'
import CommonModal from '@/components/common/modal/CommonModal'
import { logout } from '@/utils/auth'
import { useUserInfoQuery } from '@/hooks/queries/user'
import { useUserStore } from '@/store/userStore'
import { useTypeTestStore } from '@/store/typeTestStore'
import { useAppStore } from '@/store/appStore'
import { isWebView, checkAppPasscode } from '@/utils/webViewBridge'
import { useScrollToTop } from '@/hooks/useScrollToTop'

const MyPage = () => {
  useScrollToTop()

  const navigate = useNavigate()
  const userStore = useUserStore()
  const { setTypeResultFrom } = useTypeTestStore()
  const { hasAppPassword } = useAppStore()
  const { data: userInfo } = useUserInfoQuery(userStore.userInfo.user_id)
  const [setPasswordIsDone, setSetPasswordIsDone] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  useEffect(() => {
    const checkPasswordStatus = async () => {
      if (isWebView()) {
        // 웹뷰 환경에서는 브릿지를 통해 앱 비밀번호 설정 상태 확인
        const passcodeSet = await checkAppPasscode()
        setSetPasswordIsDone(passcodeSet)
      } else {
        // 웹 환경에서는 Zustand store에서 비밀번호 설정 상태 확인
        setSetPasswordIsDone(hasAppPassword())
      }
    }

    checkPasswordStatus()

    // 페이지 포커스 시 상태 다시 확인
    const handleFocus = () => {
      checkPasswordStatus()
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const handleLogoutClick = () => {
    setShowLogoutModal(true)
  }

  const handleConfirmLogout = () => {
    // logout 함수가 모든 정리 작업(FCM, 쿠키, 토큰, localStorage)을 수행하고 로그인 페이지로 이동
    logout()
  }

  const handleCancelLogout = () => {
    setShowLogoutModal(false)
  }

  return (
    <>
      <NormalTopBar
        title='MY'
        textClassName='text-xl text-text-header font-semibold'
        boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
      />
      <div className='min-h-screen p-3 overflow-y-hidden flex flex-col gap-3'>
        <CardOne className='flex flex-col gap-2 px-3 py-5 bg-white rounded-2xl'>
          <p id='profile-heading' className='text-gray-500 font-medium text-sm mb-1'>
            프로필
          </p>
          <div className='flex justify-between items-center'>
            <p className='text-sm font-bold text-gray-700'>{userInfo?.data.nickname}</p>
            <button
              className='text-amber-500 transition-colors text-xs font-medium flex items-center p-0'
              onClick={() => navigate('/my/profile-setting')}
            >
              <span>프로필 수정</span>
              <ChevronRight className='h-4 w-4 ml-1' />
            </button>
          </div>
          {/* 현재 mvp에서는 상담사 선택 X */}
          {/* <hr className='border-amber-200' />
          <div className='flex justify-between items-center'>
            <div>
              <p className='text-sm font-medium text-gray-700'>나의 상담사</p>
            </div>
            <button
              className='text-amber-500 text-xs font-medium flex items-center'
              onClick={() => navigate('/my/select-counselor')}
            >
              <span>상담사 변경</span>
              <ChevronRight className='h-4 w-4 ml-1' />
            </button>
          </div> */}
        </CardOne>
        <CardOne className='flex flex-col gap-2 px-3 py-5 bg-white rounded-2xl'>
          <p id='study-type-heading' className='text-gray-500 font-medium text-sm mb-1'>
            공부유형 검사
          </p>
          <div
            className='flex justify-between items-center'
            role='button'
            onClick={() => {
              if (userInfo?.data.type16_id !== null) {
                setTypeResultFrom('my')
                navigate('/type-test/result')
              } else {
                navigate('/type-test')
              }
            }}
          >
            <p className='text-sm font-medium text-gray-700'>타입검사</p>
            <button className='flex items-center px-2'>
              <span className={`text-xs ${userInfo?.data.type16_id !== null ? 'text-[#4C4FFF]' : 'text-amber-500'}`}>
                {userInfo?.data.type16_id !== null ? '진행완료' : '미진행'}
              </span>
            </button>
          </div>
        </CardOne>
        <CardOne className='flex flex-col gap-2 px-3 py-5 bg-white rounded-2xl'>
          <p id='app-settings-heading' className='text-gray-500 font-medium text-sm mb-1'>
            앱 설정
          </p>
          <div
            className='flex justify-between items-center'
            role='button'
            onClick={() => navigate('/my/password-setting')}
          >
            <p className='text-sm font-medium text-gray-700'>앱 비밀번호</p>
            <button className='flex items-center px-2'>
              <span className={`text-xs ${setPasswordIsDone ? 'text-[#4C4FFF]' : 'text-amber-500'}`}>
                {setPasswordIsDone ? '설정됨' : '미설정'}
              </span>
            </button>
          </div>
          <hr className='border-amber-200' />
          <div
            className='flex justify-between items-center'
            role='button'
            onClick={() => {
              navigate('/my/push-setting')
            }}
          >
            <p className='text-sm font-medium text-gray-700'>푸시알림</p>
          </div>
          <hr className='border-amber-200' />
          <div className='flex justify-between items-center' role='button' onClick={handleLogoutClick}>
            <p className='text-sm font-medium text-gray-700'>로그아웃</p>
          </div>
        </CardOne>
        <CardOne className='flex flex-col gap-2 px-3 py-5 bg-white rounded-2xl'>
          <p id='terms-heading' className='text-gray-500 font-medium text-sm mb-1'>
            이용약관
          </p>
          <div
            className='flex justify-between items-center rounded-lg transition-colors'
            role='button'
            onClick={() => navigate('/my/privacy')}
          >
            <p className='text-sm font-medium text-gray-700'>개인정보 취급방침</p>
            <ChevronRight className='h-5 w-5 text-gray-300' />
          </div>
          <hr className='border-amber-200' />
          <div
            className='flex justify-between items-center rounded-lg transition-colors'
            role='button'
            onClick={() => navigate('/my/service-usage')}
          >
            <p className='text-sm font-medium text-gray-700'>서비스 이용약관</p>
            <ChevronRight className='h-5 w-5 text-gray-300' />
          </div>
        </CardOne>
        <CardOne className='flex flex-col gap-2 px-3 py-5 bg-white rounded-2xl'>
          <div
            className='flex justify-between items-center'
            role='button'
            onClick={() => {
              navigate('/my/secession')
            }}
          >
            <p className='text-sm font-medium text-gray-700'>회원 탈퇴</p>
          </div>
        </CardOne>
        <div className='pb-23'>
          <BottomNavBar />
        </div>
      </div>
      {showLogoutModal && (
        <CommonModal
          onClose={handleCancelLogout}
          title='로그아웃'
          topButtonText='로그아웃'
          bottomButtonText='취소'
          topButtonOnClick={handleConfirmLogout}
          bottomButtonOnClick={handleCancelLogout}
          modalContent={
            <div className='mb-4'>
              <p className='text-center text-gray-700 text-sm'>정말로 로그아웃하시겠습니까?</p>
            </div>
          }
        />
      )}
    </>
  )
}

export default MyPage
